import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { downloadCsv } from './csv'

/** Banka raporu: bankaya göre giriş/çıkış, ödenen faiz ve masraflar, banka hesaplarından geçen gelir/giderler, kredi borcu. */
export const BANK_REPORT_PERM = 'finance:report:bank'

export type BankCosts = { commission: string; card: string; loan: string; cashAdvance: string; other: string; total: string }
export type BankReportRow = {
  bankId: string
  name: string
  logoUrl: string
  inflow: string
  outflow: string
  transferIn: string
  transferOut: string
  costs: BankCosts
  categories: { categoryId: string; name: string; direction: 'in' | 'out'; amount: string }[]
  loanDebt: string
  advanceDebt: string
}
export type BankReport = { from: string; to: string; banks: BankReportRow[]; totals: BankReportRow }

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const mapRow = (r: Row): BankReportRow => {
  const c = (r.costs ?? {}) as Row
  return {
    bankId: s(r.bank_id),
    name: s(r.name),
    logoUrl: s(r.logo_url),
    inflow: s(r.inflow),
    outflow: s(r.outflow),
    transferIn: s(r.transfer_in),
    transferOut: s(r.transfer_out),
    costs: { commission: s(c.commission), card: s(c.card), loan: s(c.loan), cashAdvance: s(c.cash_advance), other: s(c.other), total: s(c.total) },
    categories: (Array.isArray(r.categories) ? (r.categories as Row[]) : []).map((x) => ({
      categoryId: s(x.category_id),
      name: s(x.name),
      direction: x.direction === 'in' ? 'in' : 'out',
      amount: s(x.amount),
    })),
    loanDebt: s(r.loan_debt),
    advanceDebt: s(r.cash_advance_debt),
  }
}

export const useBankReport = (from: string, to: string, bankId: string) =>
  useQuery({
    queryKey: ['finance', 'bank-report', from, to, bankId],
    queryFn: async (): Promise<BankReport> => {
      const r = await api.get<Row>('/api/v1/finance/reports/banks', { from, to, bank_id: bankId })
      return { from: s(r.from), to: s(r.to), banks: (Array.isArray(r.banks) ? (r.banks as Row[]) : []).map(mapRow), totals: mapRow((r.totals ?? {}) as Row) }
    },
  })

const csvNum = (v: string) => Number(v || 0).toFixed(2).replace('.', ',')

export function exportBankReportCsv(rep: BankReport, header: string[]) {
  downloadCsv(
    `banka-raporu-${rep.from}_${rep.to}.csv`,
    header,
    [...rep.banks, rep.totals].map((b) => [
      b.name,
      csvNum(b.inflow),
      csvNum(b.outflow),
      csvNum(b.transferIn),
      csvNum(b.transferOut),
      csvNum(b.costs.commission),
      csvNum(b.costs.card),
      csvNum(b.costs.loan),
      csvNum(b.costs.cashAdvance),
      csvNum(b.costs.other),
      csvNum(b.costs.total),
      csvNum(b.loanDebt),
      csvNum(b.advanceDebt),
    ]),
  )
}
